import React, {useEffect, useState} from "react";
import {LookDataType, LookItemType} from "./LookTypes";
import SwiperBuilder from "../crossSelling/SwiperBuilder";
import {BreakpointType} from "../../molecules/SwiperTypes";
import {useTranslation} from "react-i18next";
import {fetchProductRelatedLooks, fetchProductsFromInternalApi, ResponseError} from "./stlHelper";
import LookDetail from "./LookDetail";
import {useMediaQuery} from "react-responsive";
import {queryBreakpointMedium} from "./stlHelper";
import {useCustomerSelector} from "../../../store";

type LookWithProduct = {
  look: LookDataType;
  lookItems: LookItemType[];
};

/* eslint-disable no-underscore-dangle */
// 1.17 slidesPerView at breakpoint 768, to better align with the 3.5-slidesPerView cross-selling-swipers
const sliderBreakpoints: BreakpointType = {
  0: {
    slidesPerView: 1.5,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
  },
  768: {
    slidesPerView: 1.17,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
  },
  1132: {
    slidesPerView: 1,
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
  },
};

const ProductRelatedLooks: React.FC<{itemId: string; containerLink?: string}> = ({itemId, containerLink}) => {
  const {t} = useTranslation();
  const {customer} = useCustomerSelector();
  const [looks, setLooks] = useState<LookWithProduct[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery({query: queryBreakpointMedium});

  useEffect(() => {
    void fetchProductRelatedLooks(itemId)
      .then(async (results) => {
        const fetchedLooks: LookWithProduct[] = [];

        for (const result of results) {
          // fetch lookItems (linked products) from our internal shop data
          const resultLookItems = await fetchProductsFromInternalApi(result, customer);

          // push only looks with more than one lookItem (linked product) into the array
          if (resultLookItems.length > 1) {
            fetchedLooks.push({look: result, lookItems: resultLookItems});
          }
        }

        if (fetchedLooks.length > 0) {
          setLooks(fetchedLooks);
          setLoading(false);
        }
      })
      .catch((error: ResponseError) => {
        // eslint-disable-next-line no-console
        if (__DEVELOPMENT__) console.error("DEVELOP:", JSON.stringify(error.data));
      });
  }, []);

  if (!looks) {
    return;
  }

  return (
    <div className="stl-pdp-wrapper">
      {loading ? (
        <div className="swiper-lazy-preloader">{/* loading spinner container */}</div>
      ) : (
        <>
          {looks.length > 1 ? (
            <SwiperBuilder
              link={
                containerLink && (
                  <a href={containerLink} className={"swiper-cross-selling__title"}>
                    {t("shopthelook.discoverAllLooks")}
                  </a>
                )
              }
              title={t("shopthelook.looksWithThisProduct--quantity", {quantity: looks.length})}
              breakpoints={sliderBreakpoints}
            >
              {looks.map((slide, key) => (
                <div key={key} className="stl-detail-slide">
                  <LookDetail look={slide.look} lookItems={slide.lookItems} isLookDetailPageItem={false} />
                  {isMobile && (
                    <a href={"/shopthelook/look.html?look-id=" + slide.look._id} className="stl-detail-slide-link" />
                  )}
                </div>
              ))}
            </SwiperBuilder>
          ) : (
            <div className="stl-detail-slide-single">
              <div className="container-title">
                <h2 className="swiper-cross-selling__title fill">{t("shopthelook.oneLookWithThisProduct")}</h2>
                {containerLink && (
                  <a href={containerLink} className={"swiper-cross-selling__title"}>
                    {t("shopthelook.discoverAllLooks")}
                  </a>
                )}
              </div>
              <div className="stl-detail-slide">
                <LookDetail look={looks[0].look} lookItems={looks[0].lookItems} isLookDetailPageItem={false} />
                {isMobile && (
                  <a href={"/shopthelook/look.html?look-id=" + looks[0].look._id} className="stl-detail-slide-link" />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductRelatedLooks;
